import React, { useEffect } from 'react';
import SearchContainer from 'containers/IdentificationPage/SearchContainer';
import SearchResults from 'containers/IdentificationPage/SearchResults';
import {
  requestSearchClaims,
  requestSupportDocuments,
  resetSearchClaimsStore,
  setLossDate,
  setSelectedClaim,
} from 'containers/IdentificationPage/actions';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { makeSelectSearchClaims } from 'containers/IdentificationPage/selectors';
import { isEmpty } from 'lodash';
import {
  SearchClaims,
  SearchQuery,
} from 'containers/IdentificationPage/typings';
import Stepper from 'containers/AppContainer/Stepper';
import {
  isClaimRejected,
  shouldDisplayExistingClaimIndexationPanel,
  shouldDisplaySearchResult,
  filterNotRcClaims,
} from 'containers/IdentificationPage/utils';
import { resetPreopenClaim } from 'containers/PreOpeningPage/actions';
import ClaimWithNotIndexedPV from 'containers/IdentificationPage/SearchResults/ClaimWithNotIndexedPV';
import { makeSelectListings } from 'containers/AppContainer/selectors';
import { Listings } from 'containers/AppContainer/typings';
import {
  requestGetReportsPolicy,
  resetUploadDocumentStore,
} from 'containers/UploadDocuments/actions';
import { CLAIM_STATUS } from 'containers/data-structure';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';

export interface IdentificationPageProps {
  requestSearchClaims: Function;
  searchClaims: SearchClaims;
  resetSearchClaimsStore: Function;
  resetPreopenClaim: Function;
  setLossDate: Function;
  listings: Listings;
  resetUploadDocumentStore: Function;
  setSelectedClaim: Function;
  requestSupportDocuments: Function;
  requestGetReportsPolicy: Function;
}

const IdentificationPage: React.FC<IdentificationPageProps> = (
  props,
): JSX.Element => {
  useEffect(() => {
    props.resetPreopenClaim();
    props.resetSearchClaimsStore();
    // empty store on unmount
    return () => {
      props.resetUploadDocumentStore();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchQuery, setSearchQuery] = React.useState<SearchQuery>({
    lossDate: null,
    registrationNumber: null,
    policyNumber: null,
    claimNumber: null,
    assuredName: null,
    assuredCIN: null,
    victimName: null,
    courtNumber: null,
  });

  const submitSearch = () => {
    props.resetSearchClaimsStore();
    props.requestSearchClaims(searchQuery);
    props.setLossDate(searchQuery.lossDate);
  };
  const enablePreOpenClaim = (searchClaims: SearchClaims) => {
    let codeIntervenants: string[] = [];
    if (
      isEmpty(
        searchClaims.corporeal?.filter(
          c =>
            c.status === CLAIM_STATUS.PENDING ||
            c.status === CLAIM_STATUS.PENDING_INFO_MODIFICATION ||
            c.status === CLAIM_STATUS.VALIDATED ||
            c.status === CLAIM_STATUS.PENDING_APPROVAL,
        ),
      )
    ) {
      codeIntervenants = searchClaims.corporeal
        ?.filter(c => c.status === CLAIM_STATUS.REJECTED)
        .map(c => c.codeLawyer);
      if (
        !isEmpty(codeIntervenants) &&
        codeIntervenants.includes(currentIntervenantStorage.get()?.sagis_code)
      ) {
        return !featureService.hasFeature(
          FEATURES.LAWYER_REJECTED_CLAIM_UPDATE,
        );
      }
      return true;
    }
    return false;
  };
  return (
    <div>
      <Stepper />
      <SearchContainer
        searchQuery={searchQuery}
        onChangeSearchQuery={setSearchQuery}
        submitSearch={submitSearch}
        isManager={false}
      />
      {shouldDisplayExistingClaimIndexationPanel(
        filterNotRcClaims(props.searchClaims, props.listings),
      ) && (
        <ClaimWithNotIndexedPV
          corporealClaimDetails={filterNotRcClaims(
            props.searchClaims,
            props.listings,
          )}
          listings={props.listings}
          setSelectedClaim={props.setSelectedClaim}
          enablePreOpenClaim={enablePreOpenClaim(
            filterNotRcClaims(props.searchClaims, props.listings),
          )}
          isPreOpeningManager={false}
          requestSearchClaims={props.requestSearchClaims}
        />
      )}
      {shouldDisplaySearchResult(
        props.listings,
        filterNotRcClaims(props.searchClaims, props.listings),
      ) && (
        <SearchResults
          policyExists={!isEmpty(props.searchClaims?.sagis?.policy)}
          claimExists={
            !isEmpty(props.searchClaims.corporeal) &&
            !isClaimRejected(props.searchClaims)
          }
          searchClaim={filterNotRcClaims(props.searchClaims, props.listings)}
          requestSupportDocuments={props.requestSupportDocuments}
          enablePreOpenClaim={enablePreOpenClaim(props.searchClaims)}
          isPreOpeningManager={false}
          listings={props.listings}
          requestGetReportsPolicy={props.requestGetReportsPolicy}
        />
      )}
    </div>
  );
};

const mapDispatchToProps = {
  requestSearchClaims,
  resetSearchClaimsStore,
  resetPreopenClaim,
  setLossDate,
  resetUploadDocumentStore,
  setSelectedClaim,
  requestSupportDocuments,
  requestGetReportsPolicy,
};

const mapStateToProps = createStructuredSelector({
  searchClaims: makeSelectSearchClaims(),
  listings: makeSelectListings(),
});

export { IdentificationPage as IdentificationPageTest };
export default connect(mapStateToProps, mapDispatchToProps)(IdentificationPage);
